import React, { useState, useEffect } from "react";
import { Grid, Paper, Typography, Divider, Box } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import { backend } from "../../api"; // Importing the backend utility

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
  },
  paper: {
    padding: theme.spacing(2),
  },
  bagContent: {
    maxHeight: 300,
    color: "#fff",
    overflowY: "auto",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    // Webkit-based browsers
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#2e2e2e",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "10px",
      border: "2px solid #2e2e2e",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    // Firefox
    "& scrollbar-width": "thin",
    "& scrollbar-color": `${theme.palette.primary.main} #2e2e2e`,
  },
  productContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(-2),
  },
  image: {
    width: "70%",
    height: "100px",
    objectFit: "contain",
  },
  totalBox: {
    marginTop: theme.spacing(3),
    display: "flex",
    color: "#fff",
    justifyContent: "space-between",
    alignItems: "center",
  },
  divider: {
    backgroundColor: "#fff",
  },
  textDetails: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  headingDetails: {
    fontSize: "0.8rem",
    color: "#fff",
  },
  priceDetails: {
    color: "#FECE00",
  },
  preOrderLabel: {
    display: "inline-block",
    color: "yellow",
    fontSize: "11px",
    fontWeight: "600",
    padding: "2px 6px",
  },
}));

export default function CheckoutBag() {
  const classes = useStyles();
  const [cartItems, setCartItems] = useState([]);
  const [salesTax, setSalesTax] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  // const [stripeProcessingCharge, setStripeProcessingCharge] = useState(0);
  const [hasHardcopyBooks, setHasHardcopyBooks] = useState(false);
  const shippingCost = 5.9;
  const additionalShippingCostPerItem = 2;

  // const STRIPE_PERCENTAGE = 2.9 / 100;
  // const STRIPE_FIXED = 0.3;

  const fetchTaxPercentage = async () => {
    try {
      const response = await backend.get("/amount", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      });
      const data = response.data;
      const taxPercentage = data.data[0].tax;
      setTaxPercentage(taxPercentage);
    } catch (error) {
      console.error("Error fetching tax percentage:", error);
    }
  };

  useEffect(() => {
    fetchTaxPercentage();
  }, []);

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const response = await backend.get("/shopCart", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        });

        const data = response.data;
        setCartItems(data.data);
        const hasHardcopy = data.data.some(
          (item) =>
            item.cartItemType === "BOOK" && item.bookOrderType !== "EBOOK"
        );
        setHasHardcopyBooks(hasHardcopy);
      } catch (error) {
        console.error("Error fetching cart items:", error);
      }
    };

    fetchCartItems();
  }, []);

  const totalPrice = cartItems.reduce((accumulator, item) => {
    const quantity = item.quantity || 1; // Default to 1 if quantity is not available
    if (item.cartItemType === "ARTICLE") {
      return (
        accumulator +
        item.articleId.per_page_price * item.articleId.number_of_pages
      );
    } else if (item.cartItemType === "BOOK") {
      if (item.bookOrderType === "HARDCOPY") {
        return accumulator + item.bookId.price.hardcover_price * quantity;
      } else if (item.bookOrderType === "EBOOK") {
        return accumulator + item.bookId.price.digital_price;
      } else if (item.bookOrderType === "BOTH") {
        return (
          accumulator +
          (item.bookId.price.hardcover_price - 5) * quantity +
          (item.bookId.price.digital_price - 5)
        );
      }
    }
    return accumulator;
  }, 0);

  const totalQuantity = cartItems.reduce((accumulator, item) => {
    const quantity = item.quantity || 1; // Default to 1 if quantity is not available
    if (
      item.cartItemType === "BOOK" &&
      (item.bookOrderType === "HARDCOPY" || item.bookOrderType === "BOTH")
    ) {
      return accumulator + quantity;
    }
    return accumulator;
  }, 0);

  const shippingCostToApply = cartItems.some(
    (item) =>
      item.cartItemType === "BOOK" &&
      (item.bookOrderType === "HARDCOPY" || item.bookOrderType === "BOTH")
  )
    ? shippingCost + (totalQuantity - 1) * additionalShippingCostPerItem
    : 0;

  useEffect(() => {
    if (totalPrice) {
      const calculatedSalesTax = totalPrice * (taxPercentage / 100);
      setSalesTax(calculatedSalesTax);
    }
  }, [totalPrice, taxPercentage]);

  useEffect(() => {
    if (totalPrice || salesTax || shippingCostToApply) {
      const costBeforeStripeFee = totalPrice + salesTax + shippingCostToApply;
      // const totalAmountFee =
      //   (costBeforeStripeFee + STRIPE_FIXED) / (1 - STRIPE_PERCENTAGE);
      // const calculatedStripeFee = totalAmountFee - costBeforeStripeFee;

      // setStripeProcessingCharge(calculatedStripeFee);
      setTotalCost(costBeforeStripeFee);
    }
  }, [totalPrice, salesTax, shippingCostToApply]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      localStorage.setItem("totalCost", totalCost.toFixed(2));
      // localStorage.setItem(
      //   "shippingCostToApply",
      //   shippingCostToApply.toFixed(2)
      // );
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [totalCost, shippingCostToApply]);

  return (
    <Grid item xs={11} md={6} className={classes.container}>
      <Paper
        className={classes.paper}
        style={{ backgroundColor: "#000", border: "1px solid #FECE00" }}
      >
        <Typography
          variant="h6"
          gutterBottom
          className={classes.headingDetails}
        >
          Your Bag ({cartItems.length})
        </Typography>
        <Divider className={classes.divider} />
        <Box className={classes.bagContent}>
          {cartItems.map((item, index) => (
            <React.Fragment key={index}>
              <Grid container className={classes.productContainer}>
                <Grid item xs={4} className={classes.productContainer}>
                  <img
                    src={
                      item.cartItemType === "ARTICLE"
                        ? "../../../assets/images/article.png"
                        : item.bookId.book_cover
                    }
                    alt="Product"
                    className={classes.image}
                  />
                </Grid>
                <div item xs={8} className={classes.textDetails}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontSize: "0.8rem", color: "#fff" }}
                  >
                    {item.cartItemType === "ARTICLE"
                      ? item.articleId.article_title
                      : item.bookId.book_title}
                    {item?.bookId?.pre_order && (
                      <span className={classes.preOrderLabel}>Pre-Order</span>
                    )}
                  </Typography>
                  {item.bookOrderType === "HARDCOPY" ||
                  item.bookOrderType === "BOTH" ? (
                    <Typography
                      variant="body2"
                      style={{ color: "#FECE00", marginTop: "2px" }}
                    >
                      <strong>Quantity: {item.quantity}</strong>
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    className={classes.priceDetails}
                    marginTop={"2px"}
                  >
                    <strong>
                      $
                      {item.cartItemType === "ARTICLE"
                        ? item.articleId.per_page_price *
                          item.articleId.number_of_pages
                        : item.bookOrderType === "HARDCOPY"
                        ? item.bookId.price.hardcover_price *
                          (item.quantity || 1)
                        : item.bookOrderType === "EBOOK"
                        ? item.bookId.price.digital_price
                        : item.bookOrderType === "BOTH"
                        ? (item.bookId.price.hardcover_price - 5) *
                            (item.quantity || 1) +
                          (item.bookId.price.digital_price - 5)
                        : ""}
                    </strong>
                  </Typography>
                </div>
              </Grid>
              <Box mt={3}>
                <Divider className={classes.divider} />
              </Box>
            </React.Fragment>
          ))}
        </Box>
        <Box mt={3}>
          <Divider className={classes.divider} />
        </Box>
        <Box mt={3} display="flex" justifyContent="space-between">
          <Typography variant="body1" className={classes.headingDetails}>
            Price excl. tax
          </Typography>
          <Typography variant="body1" className={classes.priceDetails}>
            <strong>${totalPrice.toFixed(2)}</strong>
          </Typography>
        </Box>
        {hasHardcopyBooks && (
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body1" className={classes.headingDetails}>
              Shipping
            </Typography>
            <Typography variant="body1" className={classes.priceDetails}>
              <strong>${shippingCostToApply.toFixed(2)}</strong>
            </Typography>
          </Box>
        )}
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body1" className={classes.headingDetails}>
            Sales Tax ({taxPercentage}%)
          </Typography>
          <Typography variant="body1" className={classes.priceDetails}>
            <strong>${salesTax.toFixed(2)}</strong>
          </Typography>
        </Box>
        {/* <Box display="flex" justifyContent="space-between">
          <Typography variant="body1" className={classes.headingDetails}>
          Payment Processing Fee
          </Typography>
          <Typography variant="body1" className={classes.priceDetails}>
            <strong>${stripeProcessingCharge.toFixed(2)}</strong>
          </Typography>
        </Box> */}
        <Box mt={3}>
          <Divider className={classes.divider} />
        </Box>
        <Box className={classes.totalBox}>
          <Typography variant="body1" className={classes.headingDetails}>
            Total
          </Typography>
          <Typography variant="body1" className={classes.priceDetails}>
            <strong>${totalCost.toFixed(2)}</strong>
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );
}
